import { AfterContentChecked, ChangeDetectorRef, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ComboBoxComponent, MultiSelectComponent } from '@progress/kendo-angular-dropdowns';
import { NotificationService } from 'src/app/core/services/notification.service';
import { AddressAPIService } from 'src/app/extensions/services/api/address-api.service';
import { ContractAPIService } from 'src/app/extensions/services/api/contracts-api.service';
import { DictionaryAPIService } from 'src/app/extensions/services/api/dictionary-api.service';
import { RequestAPIService } from 'src/app/extensions/services/api/request-api.service';
import { wizardSteps } from 'src/app/models/wizard-enum';
import { DateInputSize, DateInputRounded, DateInputFillMode } from '@progress/kendo-angular-dateinputs';
import {
  DictionaryAssetDocumentRequestTypeParameter,
  DictionaryContractType,
  DictionaryInsuranceTypePortalCode,
} from 'src/app/extensions/constants/dictionaryConstants/dictionaryConstants';
import { FilesHandler } from '../client-data/utils/files-handler';
import { currentTimezoneDateTime } from 'src/app/extensions/utilities';
import { RequestEvents } from 'src/app/extensions/services/api/events';
import { REQUEST_DETAIL_CATEGORY } from 'src/app/extensions/constants/commonConstants';
import { forkJoin } from 'rxjs';
import { ENV_CONFIG } from 'src/app/core/injection.token';

@Component({
  selector: 'app-request-wizard',
  templateUrl: './request-wizard.component.html',
  styleUrls: ['./request-wizard.component.scss'],
  standalone: false,
})
export class RequestWizardComponent implements OnInit, AfterContentChecked {
  @ViewChild('wizardForm') public form: NgForm;
  @ViewChild('transitedCountries') public multiselect: MultiSelectComponent;
  @ViewChild('contractItemExternalEntityId') public attributeCombobox: ComboBoxComponent;

  currentWizardStep: wizardSteps = wizardSteps.Asset_RequestType;
  public wizardStepsEnum = wizardSteps;

  delegates: app.models.Delegate[] = [];
  selectedDelegate: app.models.Delegate;
  addDelegateFormVisible: boolean = false;
  newDelegate: app.models.Delegate = {} as app.models.Delegate;

  countries: app.models.DictionaryCountry[] = [];
  filteredCountries: app.models.DictionaryCountry[] = [];
  selectedCountries: app.models.DictionaryCountry[] = [];

  fromDate: any = null;
  toDate: any = null;
  eventDate: any = null;

  insurerName: string = null;
  observations: string = '';

  requestTypes: app.models.DictionaryAssetDocumentRequestType[] = [];

  public fillModeOutline: DateInputFillMode = 'outline';
  public roundedLarge: DateInputRounded = 'large';
  public sizeLarge: DateInputSize = 'large';

  public firstQuestionFormGroup = new FormGroup({
    answear: new FormControl(),
    otherObservations: new FormControl(),
  });

  public secondQuestionFormGroup = new FormGroup({
    answear: new FormControl(),
  });

  public thirdQuestionFormGroup = new FormGroup({
    answear1: new FormControl(),
  });

  DRIVING_ABROAD: app.models.DictionaryAssetDocumentRequestType;
  DAMAGE_DELEGACY: app.models.DictionaryAssetDocumentRequestType;
  OTHER: app.models.DictionaryAssetDocumentRequestType;
  VIGNETTE: app.models.DictionaryAssetDocumentRequestType;

  contractDetails: any = { contractExternalEntityId: null, contractItemExternalEntityId: null } as any;
  documentRequestHeader: app.models.DocumentRequestHeader = {
    contractName: '',
    assetName: '',
    assetUid: '',
  } as app.models.DocumentRequestHeader;

  featureLoaded: boolean = false;

  contractsDataSource: app.models.ActiveContractModel[] = [];
  filteredContracts: app.models.ActiveContractModel[] = [];
  assetsDataSource: app.models.ContractDetailAssetModel[] = [];
  technicalServicesDataSource: app.models.PartnerModel[] = [];
  filteredTechnicalServices: app.models.PartnerModel[] = [];

  wizardEntryCompleted: boolean = false;

  canUnloadByPassForm: boolean = false;

  vignetteMinDate: Date = new Date(new Date().setHours(0, 0, 0, 0));
  requestXDocuments: app.models.RequestXDocument[] = [];
  selectedRequestType: app.models.DictionaryAssetDocumentRequestType;
  hasConfirmedInfoVerification: boolean = false;
  damageDelegacyMinDate: Date;
  damageDelegacyMaxDate: Date = new Date();

  dictionaryTransitPeriodParameters: Map<string, string> = new Map<string, string>();

  insurances: app.models.Insurance[] = [];
  transitErrorMessageTranslateKey!: string;
  eventDateErrorMessageTranslateKey!: string;
  assetErrorMessageTranslateKey!: string;
  selectedTechnicalService: app.models.PartnerModel;
  insurancePortalCodes: string[] = [DictionaryInsuranceTypePortalCode.CASCO, DictionaryInsuranceTypePortalCode.RCA];

  constructor(
    private addressAPIService: AddressAPIService,
    private requestAPIService: RequestAPIService,
    private dictionaryAPIService: DictionaryAPIService,
    private _notificationService: NotificationService,
    private _translateService: TranslateService,
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _requestAPIService: RequestAPIService,
    private _cdRef: ChangeDetectorRef,
    private _contractAPIService: ContractAPIService,
    public _filesHandler: FilesHandler,
    @Inject(ENV_CONFIG) private _envConfig: env.EnvConfigurationData
  ) {}

  ngOnInit() {
    this.contractDetails.contractExternalEntityId = this._activatedRoute.snapshot.params['contractId'];
    this.contractDetails.contractItemExternalEntityId = this._activatedRoute.snapshot.params['assetId'];
    this.contractDetails.requestTypeCode = this._activatedRoute.snapshot.params['requestTypeCode'];

    if (this.contractDetails.contractExternalEntityId && this.contractDetails.contractItemExternalEntityId) {
      this.loadContractData();
    } else if (this.contractDetails.contractExternalEntityId && !this.contractDetails.contractItemExternalEntityId) {
      this.loadContracts();
      this.loadAssets(this.contractDetails.contractExternalEntityId);

      this.currentWizardStep = wizardSteps.Asset_ChooseContractAndAsset;
    } else {
      this.loadContracts();
      this.currentWizardStep = wizardSteps.Asset_ChooseContractAndAsset;
    }

    this.loadData();
  }

  ngAfterContentChecked() {
    this._cdRef.detectChanges(); //gormGroup reset (changeRef) for summary form
  }

  loadData() {
    this.dictionaryAPIService.getDictionaryAssetDocumentRequestTypes().subscribe(x => {
      this.requestTypes = x.map(x => {
        return {
          requestTypeId: x.id,
          requestTypeCode: x.code,
          requestTypeName: x.value,
          description: x.description,
        };
      });

      this.DRIVING_ABROAD = this.requestTypes.find(x => x.requestTypeCode == 'RQST_DRIVING_ABROAD');
      this.DAMAGE_DELEGACY = this.requestTypes.find(x => x.requestTypeCode == 'RQST_DAMAGE_DELEGACY');
      this.OTHER = this.requestTypes.find(x => x.requestTypeCode == 'RQST_OTHER');
      this.VIGNETTE = this.requestTypes.find(x => x.requestTypeCode == 'RQST_VIGNETTE');

      if (
        this.contractDetails.requestTypeCode &&
        this.requestTypes.some(x => x.requestTypeCode == this.contractDetails.requestTypeCode)
      ) {
        this.firstQuestionFormGroup.value.answear = this.requestTypes.find(
          x => x.requestTypeCode == this.contractDetails.requestTypeCode
        );
        this.nextStepHandler(wizardSteps.Asset_RequestType);
      }
      this.featureLoaded = true;
    });

    if (this.contractDetails.contractExternalEntityId) {
      this.requestAPIService.getDelegates().subscribe(x => {
        this.delegates = x.map(delegate => {
          return {
            ...delegate,
            displayEditIcons: false,
            expiryDate: new Date(delegate.expiryDate),
          };
        });
      });
    }

    this.addressAPIService.getEligibleCountries().subscribe(eligibleCountries => {
      this.countries = eligibleCountries;
    });

    this.filteredCountries = [];
  }

  loadContractData() {
    this._requestAPIService
      .getDocumentRequestHeader(
        this.contractDetails.contractExternalEntityId,
        this.contractDetails.contractItemExternalEntityId
      )
      .subscribe(x => {
        this.documentRequestHeader = x;
      });
  }

  loadTechincalServices() {
    this._requestAPIService.getTechnicalServices().subscribe(technicalServices => {
      this.technicalServicesDataSource = this.filteredTechnicalServices = technicalServices;
    });
  }

  loadContracts() {
    this._contractAPIService.getActiveContracts().subscribe(contracts => {
      this.contractsDataSource = contracts
        .filter(contract => contract.dictionaryContractType.code !== DictionaryContractType.CREDIT)
        .sort((a, b) => a.number.localeCompare(b.number));

      if (this.contractsDataSource.length > 1 && this.contractDetails.contractExternalEntityId) {
        this.documentRequestHeader.contractName = this.contractsDataSource.find(
          x => x.contractId == this.contractDetails.contractExternalEntityId
        )?.number;
      }

      if (this.contractsDataSource.length === 1) {
        this.contractDetails.contractExternalEntityId = this.contractsDataSource[0].contractId;
        this.contractSelectedHandler(this.contractDetails.contractExternalEntityId);
      }
      this.filteredContracts = this.contractsDataSource;
    });
  }

  contractSelectedHandler(contractId: any) {
    let contract = this.contractsDataSource.find(x => x.contractId == contractId);
    if (contract) {
      this.documentRequestHeader = {
        ...this.documentRequestHeader,
        contractName: contract.number,
      };

      this.loadAssets(contractId);

      this.requestAPIService.getDelegates().subscribe(x => {
        this.delegates = x.map(delegate => {
          return {
            ...delegate,
            displayEditIcons: false,
            expiryDate: new Date(delegate.expiryDate),
          };
        });
      });
    }
  }

  loadAssets(contractId: string) {
    this._contractAPIService.getContractAssets(contractId).subscribe(result => {
      this.assetsDataSource = result.data;

      if (this.assetsDataSource.length === 1) {
        this.contractDetails.contractItemExternalEntityId = this.assetsDataSource[0].assetId;
        this.assetSelectedHandler(this.contractDetails.contractItemExternalEntityId);
      }
    });
  }

  loadAssetDocumentParameters(parameterCodes: string[]) {
    this.dictionaryAPIService
      .getAssetDocumentRequestTypeParamsByCodes(parameterCodes)
      .subscribe((response: Map<string, string>) => {
        this.dictionaryTransitPeriodParameters = response;

        if (this.selectedRequestType == this.DAMAGE_DELEGACY) {
          const maxPastYears =
            this.dictionaryTransitPeriodParameters[
              DictionaryAssetDocumentRequestTypeParameter.MAX_PAST_YEARS_RQST_DAMAGE_DELEGACY
            ];

          this.damageDelegacyMinDate = new Date(
            new Date().setFullYear(new Date().getFullYear() - maxPastYears, new Date().getMonth(), new Date().getDate())
          );
        }
      });
  }

  assetSelectedHandler(assetId: any) {
    let asset = this.assetsDataSource.find(x => x.assetId == assetId);
    if (asset) {
      this.documentRequestHeader.assetName = asset.name;
      this.documentRequestHeader.assetNumber = asset.number;
      this.documentRequestHeader.assetUid = asset.uid;

      if (!this.documentRequestHeader.assetNumber) {
        this.wizardEntryCompleted = false;
        this.assetErrorMessageTranslateKey = 'requests.wizard.error.invalidAssetNumber';
        return;
      }

      this.wizardEntryCompleted = true;
      this.assetErrorMessageTranslateKey = null;
    }
  }

  handleFilterContracts(value: string) {
    this.filteredContracts = this.contractsDataSource.filter(
      s => s.number.toLowerCase().indexOf(value.toLowerCase()) !== -1
    );
  }

  handleFilterTechnicalServices(value: string) {
    this.filteredTechnicalServices = this.technicalServicesDataSource.filter(
      s => s.name.toLowerCase().indexOf(value.toLowerCase()) !== -1
    );
  }

  redirectRequestsList() {
    this.form.resetForm(this.form.value);
    this.canUnloadByPassForm = false;
    this._router.navigate(['requests']);
  }

  addDelegate() {
    this.newDelegate = {} as app.models.Delegate;
    this.addDelegateFormVisible = true;
  }

  async nextStepHandler(currentStep: wizardSteps, skippedStep: any = null) {
    if (this.firstQuestionFormGroup.value.answear) {
      this.selectedRequestType = this.requestTypes.find(
        x => x.requestTypeCode === this.firstQuestionFormGroup.value.answear.requestTypeCode
      );
    }

    switch (currentStep) {
      // Step 0
      case wizardSteps.Asset_ChooseContractAndAsset:
        this.currentWizardStep = wizardSteps.Asset_RequestType;
        break;

      // Step 1
      case wizardSteps.Asset_RequestType:
        if (this.selectedRequestType.requestTypeCode === this.OTHER.requestTypeCode) {
          this.currentWizardStep = wizardSteps.Request_Preview;
          this.canUnloadByPassForm = true;
          break;
        }

        if (this.selectedRequestType.requestTypeCode === this.VIGNETTE?.requestTypeCode) {
          this.currentWizardStep = wizardSteps.Vignette_Details;
          this.canUnloadByPassForm = true;
          break;
        }

        this.currentWizardStep = wizardSteps.Asset_Delegate;
        this.canUnloadByPassForm = true;
        break;

      // Step 2
      case wizardSteps.Asset_Delegate:
        this.currentWizardStep =
          this.selectedRequestType.requestTypeCode === this.DRIVING_ABROAD.requestTypeCode
            ? wizardSteps.Asset_CountriesTransit
            : wizardSteps.Asset_DescribeEvent;

        if (this.selectedRequestType == this.DRIVING_ABROAD) {
          const transitPeriodParametersCode = [
            DictionaryAssetDocumentRequestTypeParameter.MIN_DAYS_TRANSIT_START_RQST_DAMAGE_DELEGACY,
            DictionaryAssetDocumentRequestTypeParameter.MAX_MONTHS_TRANSIT_END_RQST_DAMAGE_DELEGACY,
            DictionaryAssetDocumentRequestTypeParameter.HAS_GREEN_CARD_STEP_RQST_DRIVING_ABROAD,
          ];
          this.loadAssetDocumentParameters(transitPeriodParametersCode);
        } else if (this.selectedRequestType == this.DAMAGE_DELEGACY) {
          const transitPeriodParametersCode = [
            DictionaryAssetDocumentRequestTypeParameter.MAX_PAST_YEARS_RQST_DAMAGE_DELEGACY,
          ];
          this.loadAssetDocumentParameters(transitPeriodParametersCode);
          this.loadTechincalServices();
        }
        this.loadInsurances();
        this.selectedDelegate = this.delegates.find(x => x.delegateId == this.secondQuestionFormGroup.value.answear);
        break;

      // Step 3
      case wizardSteps.Asset_CountriesTransit:
        const hasGreenCardStep: boolean = Boolean(
          Number(
            this.dictionaryTransitPeriodParameters[
              DictionaryAssetDocumentRequestTypeParameter.HAS_GREEN_CARD_STEP_RQST_DRIVING_ABROAD
            ]
          )
        );
        this.currentWizardStep = hasGreenCardStep
          ? wizardSteps.Asset_AdditionalInsurances
          : wizardSteps.Request_Preview;
        this.canUnloadByPassForm = true;
        break;

      // Step 4
      case wizardSteps.Asset_AdditionalInsurances:
        if (skippedStep) {
          this.thirdQuestionFormGroup.reset();
        }
        this.currentWizardStep = wizardSteps.Request_Preview;
        break;

      // Step 5

      // Step 6
      case wizardSteps.Asset_DescribeEvent:
        this.currentWizardStep = wizardSteps.Request_Preview;
        this.canUnloadByPassForm = true;
        break;

      // Step 7
      case wizardSteps.Vignette_Details:
        this.currentWizardStep = wizardSteps.Request_Preview;
        this.canUnloadByPassForm = true;
        break;

      case wizardSteps.Request_Preview:
        this.currentWizardStep = wizardSteps.Request_Preview;
        await this.sendRequest(this.selectedRequestType);
        break;
    }
  }

  previousStepHandler(currentStep: wizardSteps) {
    switch (currentStep) {
      // Step 0
      case wizardSteps.Asset_ChooseContractAndAsset:
        this.redirectRequestsList();
        break;

      // Step 1
      case wizardSteps.Asset_RequestType:
        this.redirectRequestsList();
        break;

      // Step 2
      case wizardSteps.Asset_Delegate:
        this.currentWizardStep = wizardSteps.Asset_RequestType;
        this.addDelegateFormVisible = false;
        break;

      // Step 3
      case wizardSteps.Asset_CountriesTransit:
        this.currentWizardStep = wizardSteps.Asset_Delegate;
        break;

      // Step 4
      case wizardSteps.Asset_AdditionalInsurances:
        this.currentWizardStep = wizardSteps.Asset_CountriesTransit;
        break;

      // Step 5

      // Step 6
      case wizardSteps.Asset_DescribeEvent:
        this.currentWizardStep = wizardSteps.Asset_Delegate;
        break;

      // Step 7
      case wizardSteps.Vignette_Details:
        this.currentWizardStep = wizardSteps.Asset_RequestType;
        break;

      case wizardSteps.Request_Preview:
        switch (this.selectedRequestType.requestTypeCode) {
          case this.DAMAGE_DELEGACY.requestTypeCode:
            this.currentWizardStep = wizardSteps.Asset_DescribeEvent;
            break;
          case this.VIGNETTE?.requestTypeCode:
            this.currentWizardStep = wizardSteps.Vignette_Details;
            break;
          case this.DRIVING_ABROAD.requestTypeCode:
            this.currentWizardStep = wizardSteps.Asset_AdditionalInsurances;
            break;
          case this.OTHER.requestTypeCode:
            this.currentWizardStep = wizardSteps.Asset_RequestType;
            break;
        }
        break;
    }
  }

  async sendRequest(requestType: app.models.DictionaryAssetDocumentRequestType) {
    this.form.resetForm(this.form.value);
    this.canUnloadByPassForm = false;
    if (!this.isWizardRequestValid()) {
      this._notificationService.showWarning(
        this._translateService.instant('requests.wizard.notification.incompleteWizard'),
        this._translateService.instant('common.warning')
      );
      return;
    }
    let request = this.mapWizardRequestProperties(requestType);
    this.requestAPIService.saveRequest(request).subscribe(response => {
      if (!response.isSuccess) {
        this._notificationService.showError(
          this._translateService.instant('requests.wizard.notification.requestSaveFailed'),
          this._translateService.instant('common.error')
        );
        return;
      }
      const successEvent = response.response.events.find(event => event.eventType === RequestEvents.RequestSaved);
      const requestId = successEvent.eventBody.requestId;
      const hasGreenCard = successEvent.eventBody.hasGreenCard;

      this._notificationService.showSuccess(
        this._translateService.instant(
          hasGreenCard
            ? 'requests.wizard.notification.hasGreenCardMessage'
            : 'requests.wizard.notification.requestSaved'
        ),
        this._translateService.instant('common.success')
      );

      const requestsRequiringRedirect = [this.DRIVING_ABROAD.requestTypeCode, this.DAMAGE_DELEGACY.requestTypeCode];
      if (requestsRequiringRedirect.includes(this.selectedRequestType.requestTypeCode)) {
        this.redirectRequestDocuments(requestId);
        return;
      }
      this.currentWizardStep = this.wizardStepsEnum.Asset_RequestSent;
    });
  }

  showDelegate() {
    if (this.secondQuestionFormGroup?.value?.answear != null && this.currentWizardStep != wizardSteps.Asset_Delegate)
      return true;
    return false;
  }

  showOptionalInsurances() {
    if (
      this.thirdQuestionFormGroup.value.answear1 != null &&
      this.currentWizardStep != wizardSteps.Asset_AdditionalInsurances &&
      this.currentWizardStep != wizardSteps.Asset_Delegate
    ) {
      return true;
    }

    return false;
  }

  clearWizardForm() {
    this.secondQuestionFormGroup?.reset();
    this.thirdQuestionFormGroup?.reset();

    this.selectedCountries = [];
    this.eventDate = null;
    this.fromDate = null;
    this.toDate = null;
  }

  delegateSavedHandler(event: app.models.Delegate) {
    if (event.displayEditIcons == true) {
      event.displayEditIcons = false;
      this.addDelegateFormVisible = false;
      this.secondQuestionFormGroup.setValue({ answear: event.delegateId }); //autoselect edited Delegate
      return;
    }
    if (event && event.delegateId) {
      this.addDelegateFormVisible = false;
      this.secondQuestionFormGroup.setValue({ answear: event.delegateId }); //autoselect newly added Delegate
      this.delegates.push(event);
      let indexAddedDelegate = this.delegates.indexOf(event);
      let radioAddedDelegate = document.getElementById('radio-' + indexAddedDelegate);
      if (radioAddedDelegate) {
        radioAddedDelegate.click();
      }
    }
  }

  handleFilter(value: string) {
    if (value.length >= 3) {
      this.filteredCountries = this.countries.filter(
        s => s.dictionaryCountryName.toLowerCase().indexOf(value.toLowerCase()) !== -1
      );
    }
  }

  selectCountryHandler(value: any) {
    this.filteredCountries = [];
  }

  removeSelectedCountry(id: string) {
    let country = this.selectedCountries.find(x => x.dictionaryCountryId == id);
    let indexOfCountry = this.selectedCountries.indexOf(country);
    if (indexOfCountry > -1) {
      this.selectedCountries.splice(indexOfCountry, 1);
    }
  }

  toggleDatePicker(datepicker) {
    datepicker.toggle();
  }

  checkTransitedCountriesForm() {
    if (this.selectedCountries.length <= 0) {
      return true;
    }

    if (this.fromDate == null || this.toDate == null || this.transitErrorMessageTranslateKey != null) {
      return true;
    }

    return false;
  }

  checkDamageEventDetails() {
    const isTechnicalServiceInvalid: boolean =
      this.technicalServicesDataSource.length > 0 ? this.selectedTechnicalService?.externalEntityId == null : false;

    if (
      this.eventDate == null ||
      this.insurerName == null ||
      this.eventDateErrorMessageTranslateKey != null ||
      isTechnicalServiceInvalid
    ) {
      return true;
    }

    return false;
  }

  isWizardRequestValid() {
    if (
      this.firstQuestionFormGroup?.value?.answear == this.DRIVING_ABROAD &&
      this.secondQuestionFormGroup.value.answear != null &&
      this.selectedCountries?.length > 0 &&
      this.fromDate != null &&
      this.toDate != null
    ) {
      return true;
    }

    if (
      this.firstQuestionFormGroup?.value?.answear == this.DAMAGE_DELEGACY &&
      this.secondQuestionFormGroup.value.answear != null &&
      this.eventDate != null &&
      this.insurerName != null &&
      (this.technicalServicesDataSource.length > 0 ? this.selectedTechnicalService != null : true)
    ) {
      return true;
    }

    if (
      this.firstQuestionFormGroup?.value?.answear == this.OTHER &&
      this.firstQuestionFormGroup?.value?.otherObservations != null
    ) {
      return true;
    }

    if (this.firstQuestionFormGroup?.value?.answear == this.VIGNETTE && this.eventDate != null) {
      return true;
    }

    return false;
  }

  mapWizardRequestProperties(requestType: app.models.DictionaryAssetDocumentRequestType) {
    let requestData = {
      dictionaryRequestTypeId: requestType.requestTypeId,
      contractExternalEntityId: this.contractDetails.contractExternalEntityId,
      contractItemExternalEntityId: this.contractDetails.contractItemExternalEntityId,
      contractNumber: this.documentRequestHeader.contractName,
      assetName: this.documentRequestHeader.assetName,
    } as any;

    if (requestType.requestTypeCode == 'RQST_DRIVING_ABROAD') {
      requestData.delegateId = this.secondQuestionFormGroup.value.answear;
      requestData.hasGreenCard = this.thirdQuestionFormGroup.value.answear1;
      requestData.transitedCountries = this.selectedCountries.map(x => {
        return { requestXTransitedCountryId: null, countryExternalEntityId: x.dictionaryCountryId };
      });
      requestData.transitFromDate = currentTimezoneDateTime(this.fromDate);
      requestData.transitToDate = currentTimezoneDateTime(this.toDate);
    }

    if (requestType.requestTypeCode == 'RQST_DAMAGE_DELEGACY') {
      requestData.delegateId = this.secondQuestionFormGroup.value.answear;
      requestData.eventDate = currentTimezoneDateTime(this.eventDate);
      requestData.insurerName = this.insurerName;
      requestData.observations = this.observations;
      requestData.technicalServiceExternalEntityId = this.selectedTechnicalService?.externalEntityId;
    }

    if (requestType.requestTypeCode == 'RQST_OTHER') {
      requestData.eventDate = this.eventDate;
      requestData.observations = this.firstQuestionFormGroup.value.otherObservations;
    }

    if (requestType.requestTypeCode == 'RQST_VIGNETTE') {
      requestData.eventDate = this.eventDate;
      requestData.observations = this.observations;
    }

    requestData.requestXDocuments = this.requestXDocuments;
    requestData.hasConfirmedInfoVerification = this.hasConfirmedInfoVerification;

    return requestData;
  }

  debug() {
    let wizardForm = {
      requestType: this.firstQuestionFormGroup.value.answear,
      delegatePerson: this.secondQuestionFormGroup.value.answear,
      hasGreenCard: this.thirdQuestionFormGroup.value.answear1,
      transitedCountries: this.selectedCountries.map(x => {
        return { countryExternalEntityId: x.dictionaryCountryId };
      }),
      fromDate: this.fromDate,
      toDate: this.toDate,
      eventDate: this.eventDate,
      insurerName: this.insurerName,
      observations: this.observations,
    } as any;
  }

  toggleComboboxAsset(disable: boolean) {
    if (disable == true) {
      return;
    }
    this.attributeCombobox.toggle(true);
  }

  public editDelegateHandler(selectedDelegate: app.models.Delegate) {
    this.newDelegate = selectedDelegate;
    this.addDelegateFormVisible = true;
  }

  public removeDelegateHandler(selectedDelegate: app.models.Delegate) {
    this.delegates.forEach((element, index) => {
      if (element.delegateId == selectedDelegate.delegateId) {
        this.delegates.splice(index, 1);
      }
    });

    this._requestAPIService.removeDelegate(selectedDelegate.delegateId).subscribe(response => {});
  }

  checkVignetteDetails() {
    if (this.eventDate == null) {
      return true;
    }

    return false;
  }

  checkTransitContractInsuranceValidity(transitDate: Date) {
    if (JSON.parse(this._envConfig.BYPASS_RESTRICTIONS_ASSET_DOCUMENT_REQUEST || 'false')) {
      return;
    }

    this.transitErrorMessageTranslateKey = null;

    if (
      !(
        new Date(this.documentRequestHeader.contractStartDate) <= new Date(transitDate) &&
        new Date(transitDate) <= new Date(this.documentRequestHeader.contractEndDate)
      )
    ) {
      this.transitErrorMessageTranslateKey = 'requests.wizard.error.invalidContract';
      return;
    }

    const hasValidInsurances = this.insurancePortalCodes.every(portalCode =>
      this.insurances.some(
        insurance =>
          insurance.dictionaryInsuranceType.portalCode == portalCode &&
          new Date(transitDate) >= new Date(insurance.startDate) &&
          new Date(transitDate) <= new Date(insurance.endDate)
      )
    );

    if (!hasValidInsurances) this.transitErrorMessageTranslateKey = 'requests.wizard.error.invalidInsurances';
  }

  checkTransitFromDateValidity(transitStartDate: Date) {
    this.adjustEndDateHandler();
    this.checkTransitContractInsuranceValidity(transitStartDate);
  }

  checkEventDateValidity(eventDate: Date) {
    if (JSON.parse(this._envConfig.BYPASS_RESTRICTIONS_ASSET_DOCUMENT_REQUEST || 'false')) {
      return;
    }

    this.eventDateErrorMessageTranslateKey = null;

    const isEventDateValid = this.insurances.some(
      i =>
        i.dictionaryInsuranceType.portalCode == DictionaryInsuranceTypePortalCode.CASCO &&
        new Date(eventDate) >= new Date(i.startDate) &&
        new Date(eventDate) <= new Date(i.endDate)
    );
    if (!isEventDateValid) this.eventDateErrorMessageTranslateKey = 'requests.wizard.error.invalidEventDate';
  }

  loadInsurances() {
    this._contractAPIService
      .getContractInsurancesLite(
        this.contractDetails.contractExternalEntityId,
        this.contractDetails.contractItemExternalEntityId,
        this.insurancePortalCodes
      )
      .subscribe({
        next: response => {
          this.documentRequestHeader.contractStartDate = new Date(response.contractStartDate);
          this.documentRequestHeader.contractEndDate = new Date(response.contractEndDate);
          this.insurances = response.insurances;
        },
      });
  }

  disabledPastVignetteMinDate = (date: Date) => {
    return date < this.vignetteMinDate;
  };

  disableTransitFromDates = (date: Date) => {
    const minStartTransitDateParameter =
      +this.dictionaryTransitPeriodParameters[
        DictionaryAssetDocumentRequestTypeParameter.MIN_DAYS_TRANSIT_START_RQST_DAMAGE_DELEGACY
      ];
    const minStartTransitDate = new Date(new Date().setDate(new Date().getDate() + minStartTransitDateParameter));
    minStartTransitDate.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return date < minStartTransitDate;
  };

  disableTransitToDates = (date: Date) => {
    const empowermentActiveMonths =
      +this.dictionaryTransitPeriodParameters[
        DictionaryAssetDocumentRequestTypeParameter.MAX_MONTHS_TRANSIT_END_RQST_DAMAGE_DELEGACY
      ];
    const maxDateAllowed = new Date(
      new Date(this.fromDate).setMonth(new Date(this.fromDate).getMonth() + empowermentActiveMonths)
    );
    const selectedStartDate = new Date(this.fromDate);

    selectedStartDate.setHours(0, 0, 0, 0);
    maxDateAllowed.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);

    return date < selectedStartDate || date > maxDateAllowed;
  };

  adjustEndDateHandler() {
    if (this.toDate < this.fromDate) this.toDate = null;
  }

  editWizardStepHandler(step: wizardSteps) {
    this.currentWizardStep = step;
  }

  redirectRequestDocuments(requestId: string) {
    this.form.resetForm(this.form.value);
    this.canUnloadByPassForm = false;
    this._router.navigate(['requests', requestId], {
      state: { data: REQUEST_DETAIL_CATEGORY.DOCUMENTS },
    });
  }
}
